/**
 * A sensible shape for a song played through, in one go: the usual worship arc, read off each part's
 * name and where it falls.
 */

import { FULL, NORMAL, PAD_KEEP, SOFT, STRONG } from './chartIntensity';
import { isSectionPart } from './partNumbers';

const CHORUSES = ['Chorus', 'Refrain'];

const repeatsOf = (part) => Math.max(1, Math.round(part.repeatCount) || 1);

// A bridge drops and climbs back a level a pass, short of Full so the last chorus still lands on top
const bridgePasses = (count) => Array.from({ length: count }, (unused, i) => Math.min(SOFT + i, STRONG));

// A chorus that repeats lifts to Full on its last pass, the rest left to the part's own level
const climbPasses = (count) => Array.from({ length: count }, (unused, i) => (i === count - 1 ? FULL : null));

/**
 * The level each part should play through at, part by part. A mix starts the arc again at each of
 * its songs.
 *
 * @param {object[]} parts - The song's parts, in order
 * @param {function} canArrange - Whether a part plays through, and so gets a level
 * The pad is kept on from the first part that brings it in, in a song with no pad set by hand.
 *
 * @returns {Map<string, { intensity: number, passes: (number|null)[]|null, drop: boolean, pad: string|null }>}
 *   By part id
 */
export const suggestIntensities = (parts = [], canArrange = () => true) => {
  const suggested = new Map();
  const songs = [[]];

  parts.forEach((part) => {
    if (isSectionPart(part)) songs.push([]);
    else if (canArrange(part)) songs[songs.length - 1].push(part);
  });

  songs.forEach((song) => {
    const choruses = song.filter((part) => CHORUSES.includes(part.name));
    const lastChorus = choruses[choruses.length - 1];
    let verses = 0;
    let padKept = song.some((part) => part.chartPad);

    song.forEach((part) => {
      const repeats = repeatsOf(part) > 1;
      let level = { intensity: NORMAL, passes: null, drop: false, pad: null };

      if (part.name === 'Intro' || part.name === 'Outro') level.intensity = SOFT;

      if (part.name === 'Verse') {
        level.intensity = verses === 0 ? SOFT : NORMAL;
        verses += 1;
      }

      // The last of several choruses is the top of the song, with the band dropping out a beat
      // before it; one that repeats climbs there instead
      if (CHORUSES.includes(part.name)) {
        const peak = part === lastChorus && choruses.length > 1;

        level = {
          ...level,
          intensity: peak && !repeats ? FULL : STRONG,
          passes: (peak || choruses.length === 1) && repeats ? climbPasses(repeatsOf(part)) : null,
          drop: peak,
        };
      }

      if (part.name === 'Bridge') {
        level = { ...level, intensity: SOFT, passes: repeats ? bridgePasses(repeatsOf(part)) : null };
      }

      if (!padKept && Math.max(level.intensity, ...(level.passes || [])) >= STRONG) {
        level.pad = PAD_KEEP;
        padKept = true;
      }

      suggested.set(part.id, level);
    });
  });

  return suggested;
};
